<template>
  <div
    ref="starrySkyContainer"
    class="top-0 left-0 w-full h-full pointer-events-none overflow-hidden z-0 drift-capsule-modal"
  ></div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from "vue";

const props = withDefaults(
  defineProps<{
    things?: ("sparkle" | "twinkling-star" | "shooting-star")[];
  }>(),
  {
    things: () => ["sparkle", "twinkling-star", "shooting-star"],
  }
);

const starrySkyContainer = ref<HTMLElement>();
const createStarrySky = () => {
  const container = starrySkyContainer.value;
  if (!container) {
    console.log("星空容器未找到");
    return;
  }

  console.log("开始创建星空...");

  const floatingCount = 25;
  const twinklingCount = 50;
  const shootingCount = 4;

  // 清空容器
  container.innerHTML = "";

  // 创建漂浮星星
  if (props.things.includes("sparkle")) {
    for (let i = 0; i < floatingCount; i++) {
      const sparkle = document.createElement("div");
      sparkle.className = "sparkle";
      const size = Math.random() * 4 + 1.5;
      sparkle.style.width = `${size}px`;
      sparkle.style.height = `${size}px`;
      sparkle.style.left = `${Math.random() * 100}%`;
      sparkle.style.top = `${Math.random() * 100 + 100}%`;
      sparkle.style.animationDelay = `${Math.random() * 15}s`;
      sparkle.style.animationDuration = `${Math.random() * 10 + 10}s`;
      sparkle.style.zIndex = "1";
      container.appendChild(sparkle);
    }
  }

  // 创建闪烁星星
  if (props.things.includes("twinkling-star")) {
    for (let i = 0; i < twinklingCount; i++) {
      const star = document.createElement("div");
      star.className = "twinkling-star";
      const size = Math.random() * 2.5 + 1;
      star.style.width = `${size}px`;
      star.style.height = `${size}px`;
      star.style.top = `${Math.random() * 100}%`;
      star.style.left = `${Math.random() * 100}%`;
      star.style.animationDelay = `${Math.random() * 4}s`;
      star.style.animationDuration = `${Math.random() * 3 + 2}s`;
      star.style.zIndex = "1";
      container.appendChild(star);
    }
  }

  // 创建流星
  if (props.things.includes("shooting-star")) {
    for (let i = 0; i < shootingCount; i++) {
      const star = document.createElement("div");
      star.className = "shooting-star";
      star.style.top = `${Math.random() * 80 - 20}%`;
      star.style.left = `${Math.random() * 80 - 20}%`;
      star.style.animationDelay = `${Math.random() * 8 + 2}s`;
      star.style.animationDuration = `${Math.random() * 2 + 2}s`;
      star.style.zIndex = "1";
      container.appendChild(star);
    }
  }

  console.log(
    `星空创建完成: ${floatingCount + twinklingCount + shootingCount} 个元素`
  );
};

onMounted(() => {
  // 添加一些测试星星确保容器工作正常
  setTimeout(() => {
    createStarrySky();
  });
});
</script>

<style lang="postcss">
/* 使用一个可爱活泼的中文字体 */
@import url("https://fonts.googleapis.com/css2?family=ZCOOL+KuaiLe&display=swap");

.drift-capsule-modal {
  * {
    font-family: "ZCOOL KuaiLe", cursive, sans-serif;
  }

  /* 梦幻的渐变背景 */
  .celestial-background {
    background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #a1c4fd 100%);
  }

  /* --- 星空背景动画 --- */
  @keyframes sparkle-float {
    0% {
      transform: translateY(0) scale(1);
      opacity: 0.8;
    }
    100% {
      transform: translateY(-100vh) scale(1.8);
      opacity: 0;
    }
  }

  .sparkle {
    position: absolute;
    background-color: white;
    border-radius: 50%;
    box-shadow: 0 0 10px #fff, 0 0 15px #fff, 0 0 25px #ffc6ff;
    animation: sparkle-float 15s linear infinite;
    z-index: 1;
    pointer-events: none;
  }

  @keyframes twinkle {
    0%,
    100% {
      opacity: 0.3;
      transform: scale(1);
    }
    50% {
      opacity: 1;
      transform: scale(1.2);
    }
  }

  .twinkling-star {
    position: absolute;
    background-color: white;
    border-radius: 50%;
    box-shadow: 0 0 5px #fff, 0 0 10px #fecfef;
    animation: twinkle 3s ease-in-out infinite;
    z-index: 1;
    pointer-events: none;
  }

  @keyframes shooting-star-animation {
    0% {
      transform: translateX(0) translateY(0);
      opacity: 1;
    }
    100% {
      transform: translateX(120vw) translateY(120vh);
      opacity: 0;
    }
  }

  .shooting-star {
    position: absolute;
    height: 3px;
    width: 120px;
    background: linear-gradient(-45deg, #fecfef, rgba(255, 255, 255, 0));
    border-radius: 999px;
    filter: drop-shadow(0 0 8px #ff9a9e);
    animation: shooting-star-animation 3s linear infinite;
    transform: rotate(-45deg);
    z-index: 1;
    pointer-events: none;
  }

  /* 休眠仓的样式和动画 */
  .card-pack {
    position: relative;
    cursor: pointer;
    transition: transform 0.3s ease-in-out, filter 0.3s;
    filter: drop-shadow(0 0 15px rgba(255, 255, 255, 0.7));
  }

  .card-pack:hover {
    transform: scale(1.1) rotate(5deg);
    filter: drop-shadow(0 0 25px rgba(255, 255, 255, 1));
  }

  .card-pack.disabled {
    cursor: not-allowed;
    filter: grayscale(80%) drop-shadow(0 0 5px rgba(0, 0, 0, 0.5));
  }

  .card-pack.disabled:hover {
    transform: none;
  }

  .card-pack-glow {
    position: absolute;
    top: -10%;
    left: -10%;
    width: 120%;
    height: 120%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.8) 0%,
      rgba(255, 255, 255, 0) 70%
    );
    animation: pulse 2s infinite;
    z-index: -1;
  }

  @keyframes pulse {
    0% {
      transform: scale(0.9);
      opacity: 0.7;
    }
    50% {
      transform: scale(1.1);
      opacity: 1;
    }
    100% {
      transform: scale(0.9);
      opacity: 0.7;
    }
  }

  .shake {
    animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
  }

  @keyframes shake {
    10%,
    90% {
      transform: translate3d(-1px, 0, 0) rotate(-2deg);
    }
    20%,
    80% {
      transform: translate3d(2px, 0, 0) rotate(4deg);
    }
    30%,
    50%,
    70% {
      transform: translate3d(-4px, 0, 0) rotate(-6deg);
    }
    40%,
    60% {
      transform: translate3d(4px, 0, 0) rotate(6deg);
    }
  }

  .modal-overlay {
    transition: opacity 0.5s ease;
  }

  .card-reveal {
    animation: card-appear 1s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  }

  @keyframes card-appear {
    0% {
      transform: scale(0.5) rotateY(90deg);
      opacity: 0;
    }
    100% {
      transform: scale(1) rotateY(0deg);
      opacity: 1;
    }
  }

  .normal-glow {
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.9),
      0 0 25px rgba(224, 224, 224, 0.8);
  }

  .rare-glow {
    box-shadow: 0 0 15px #4ade80, 0 0 30px #22c55e, 0 0 45px #16a34a;
  }

  .uncommon-glow {
    box-shadow: 0 0 15px #60a5fa, 0 0 30px #3b82f6, 0 0 45px #2563eb;
  }
}
</style>
